import { ReactNode, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';

import { cn, useRouterBackCallback, WizardProvider } from '@restworld/utils-common';
import { Worker } from '@restworld/utility-types';
import { Modal, ModalContent } from '@restworld/ui-ds-base';

import { WizardStep } from './worker-application-wizard.components';
import { useWizardSteps } from './worker-application-wizard.constants';
import { WorkerApplicationCxtProvider, useWorkerApplicationCxt } from './worker-application-wizard.context';

const START_INDEX = 0;
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface WorkerApplicationFormWizardProps {}
export interface WizardFormType extends Partial<Worker> {
  email?: string;
  tAndC?: boolean;
  /** The optional signup box (RW-422): never sent as a worker field, it goes in `acceptance`. */
  marketingAndTracking?: boolean;
}
export const WorkerApplicationFormWizard = (props: WorkerApplicationFormWizardProps) => {
  const methods = useForm<WizardFormType>({
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      tAndC: false,
      marketingAndTracking: false,
    },
  });
  return (
    <CarouselWrapper>
      <FormProvider {...methods}>
        <WorkerApplicationCxtProvider>
          <ApplicationWizardEl>
            <WizardEl />
          </ApplicationWizardEl>
        </WorkerApplicationCxtProvider>
        <DevTool control={methods.control} />
      </FormProvider>
    </CarouselWrapper>
  );
};
export default WorkerApplicationFormWizard;
const ApplicationWizardEl = ({ children }: { children: ReactNode }) => {
  const { overlayModalContent, setOverlayModalContent /* didUserSignup */ } = useWorkerApplicationCxt();
  const { onRouterBack } = useRouterBackCallback(`/worker`);
  const onCloseCallback = useCallback(() => {
    setOverlayModalContent({ key: '', content: null, visible: false });
    if (overlayModalContent.key === 'auto-apply-job') {
      onRouterBack();
      return null;
    }
    return null;
  }, [onRouterBack, overlayModalContent.key, setOverlayModalContent]);
  return (
    <div className="w-full h-full">
      {children}
      <Modal open={overlayModalContent.visible} onOpenChange={(v) => !v && onCloseCallback()}>
        <ModalContent
          variant="center"
          className={`md:min-w-104 md:max-w-md ${
            overlayModalContent.key === 'cover-letter-entry' ? '!max-w-3xl pb-4' : ''
          } ${overlayModalContent.key === 'wizard-experience-input-confirm-modal' ? 'md:!pt-0' : ''}`}
        >
          <div className="md:w-full mx-auto">{overlayModalContent.content}</div>
        </ModalContent>
      </Modal>
    </div>
  );
};
const WizardEl = () => {
  const wizardSteps = useWizardSteps();
  return (
    // <WizardProvider startIndex={START_INDEX} urlControl stepParamName="step">
    <WizardProvider startIndex={START_INDEX} stepParamName="step">
      {[...wizardSteps].map((steps) => (
        <WizardStep key={steps.key} {...steps} />
      ))}
    </WizardProvider>
  );
};
const CarouselWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full h-full md:h-screen', className)} {...props} />
);
export * from './worker-application-wizard.constants';
export * from './worker-application-wizard.hooks';
export * from './worker-application-wizard.components';
export * from './worker-application-wizard.components/form-inputs.hooks';
